import { CALIBRATION_TYPE, CalibrationType } from '../../../config/calibration';
import { getSensorSchemaFromCategoryAndType } from '../sensors/sensorSchemas'
import type { CalibrateSensorRequest } from '../services/calibration.service'
import { trimStrings } from '../../../shared/utils/trimStrings'
import { measurementUnitFromRangeValue } from './rangeUnit'
import { matchesCategory } from '../sensors/categoryUtils'

export interface TemperatureFrequencyPoint {
  condition: 'room' | 'high'
  frequency: number
  temperature: number
}

/**
 * Transform form data to API format
 * This is business logic that should be in components/utils, not services
 */
export const transformFormDataToAPI = (
  formData: any,
  calibrationType: CalibrationType = CALIBRATION_TYPE.INITIAL
): CalibrateSensorRequest => {
  // Extract measurement range number from string (e.g., "50mm" -> 50, "150" -> 150)
  const rangeValue = typeof formData.range === 'string' 
    ? parseFloat(formData.range.replace(/[^\d.]/g, '')) 
    : parseFloat(formData.range)

  // Get the sensor schema to use its transformation function and configuration
  const schema = getSensorSchemaFromCategoryAndType(formData?.category, formData?.sensorType)

  // Validate schema provides required transformation function
  if (!schema.inputFieldConfig?.transformToRawFrequency) {
    throw new Error(`Sensor schema for category "${formData?.category}" and type "${formData?.sensorType}" does not provide transformToRawFrequency function.`)
  }

  // Use schema's transformation function
  const rawFrequencyData = schema.inputFieldConfig.transformToRawFrequency(formData, rangeValue)

  // Build temperature_frequency array
  const temperatureFrequency: TemperatureFrequencyPoint[] = []

  if (formData.roomT && formData.roomF) {
    temperatureFrequency.push({
      condition: 'room',
      temperature: parseFloat(formData.roomT),
      frequency: parseFloat(formData.roomF)
    })
  }

  if (formData.highT && formData.highTempF) {
    temperatureFrequency.push({
      condition: 'high',
      temperature: parseFloat(formData.highT),
      frequency: parseFloat(formData.highTempF)
    })
  }

  // Build input_data object with category-specific fields
  // Use the same category matching as sensorSchemas (partial match, aliases)
  // so input_data stays consistent when UI/API sends e.g. "VW Piezometer".
  const inputData: any = {
    BOL: parseFloat(formData.BOL),
    raw_frequency: rawFrequencyData,
    temperature_frequency: temperatureFrequency
  }

  // Ambient temperature, humidity, atmospheric pressure, and weather condition
  // are no longer entered per-calibration — the backend injects them
  // server-side from today's Daily Environmental Data when the calibration is created.
  if (matchesCategory(formData?.category, 'piezometer')) {
    if (formData.dwtTraceable !== undefined && formData.dwtTraceable !== null && formData.dwtTraceable !== '') {
      inputData.dwt_traceable_certificate_no = formData.dwtTraceable.toString().trim()
    }
  } else if (matchesCategory(formData?.category, 'displacement')) {
    if (formData.dwtTraceable !== undefined && formData.dwtTraceable !== null && formData.dwtTraceable !== '') {
      inputData.equipment_calibration_certificate_no = formData.dwtTraceable.toString().trim()
    }
  }
  
  // Trim every string identifier at the API boundary so accidental whitespace
  // from paste/copy (tabs, trailing spaces) never reaches the backend. Backend
  // Pydantic validators do the same — this layer just gives users immediate
  // feedback in the request payload they see and avoids needless 422s.
  const unitFromRange = measurementUnitFromRangeValue(formData?.range)

  const request: CalibrateSensorRequest = {
    serial_number: formData?.sensorId,
    category: formData?.category,
    type: formData?.sensorType,
    model: formData?.model,
    measurement_range: rangeValue,
    unit: unitFromRange || schema.unit || 'unit',
    status: 'New',
    condition: 'Good',
    is_active: true,
    calibration_type: calibrationType,
    input_data: inputData,
  }
  return trimStrings(request)
}

